import config from '../config/index.js';
import { setupAuthentication, sendAuthenticatedRequest } from './auth.js';
import { setupUserToken } from './auth_utils.js';
import { getUser } from './data/update_operations.js';
import { showProfileSetupWizardAndWait } from './ui/profile_setup_wizard.js';

const searchInput = document.getElementById('discover-search');
if (searchInput) {
  const redirect = () => {
    window.location.href = 'search.html';
  };
  searchInput.addEventListener('focus', redirect);
  searchInput.addEventListener('click', redirect);
}

async function fetchTrendingItems() {
  try {
    const res = await fetch(`${config.detectionUrl}/trending-items`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Failed to fetch trending items');
    const data = await res.json();
    const items = data.items || data.trending || data || [];
    return items.slice(0, 20);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error fetching trending items:', err);
    return [];
  }
}

async function fetchFeaturedCards() {
  try {
    const res = await fetch(`${config.detectionUrl}/featured-cards`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Failed to fetch featured cards');
    const data = await res.json();
    const cards = data.cards || data || [];
    return cards.slice(0, 20);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error fetching featured cards:', err);
    return [];
  }
}

async function fetchRecentlyAddedCards() {
  try {
    const res = await fetch(`${config.detectionUrl}/recently-added-cards`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Failed to fetch recently added cards');
    const data = await res.json();
    const cards = data.cards || data.recentlyAdded || data.recent || data || [];
    return cards.slice(0, 20);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error fetching recently added cards:', err);
    return [];
  }
}

async function fetchTrendingTweets() {
  try {
    const res = await fetch(`${config.detectionUrl}/trending-tweets`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Failed to fetch trending tweets');
    const data = await res.json();
    const tweets = data.tweets || data || [];
    return tweets.slice(0, 10);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error fetching trending tweets:', err);
    return [];
  }
}

function getAuctionImageUrl(item) {
  const candidates = [
    item?.image,
    item?.image?.imageUrl,
    item?.image?.url,
    item?.imageUrl,
    item?.image_url,
    item?.galleryURL,
    item?.galleryUrl,
    item?.pictureURLLarge,
    item?.pictureURLSuperSize,
    item?.thumbnailImages?.[0]?.imageUrl,
    item?.thumbnailImages?.[0]?.url,
    item?.additionalImages?.[0]?.imageUrl,
    item?.additionalImages?.[0]?.url,
  ];

  for (const candidate of candidates) {
    const value = Array.isArray(candidate) ? candidate[0] : candidate;
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function renderAuctionImage(container, item, card) {
  const imageUrl = getAuctionImageUrl(item);
  if (!imageUrl) {
    container.classList.add('auction-image-missing');
    card.classList.add('trending-item--no-image');
    return;
  }

  const img = document.createElement('img');
  img.src = imageUrl;
  img.alt = item.title || 'Auction listing';
  img.addEventListener(
    'error',
    () => {
      img.remove();
      container.classList.add('auction-image-missing');
      card.classList.add('trending-item--no-image');
    },
    { once: true }
  );
  container.prepend(img);
}

function getAuctionBidCount(item) {
  const candidates = [
    item?.bidCount,
    item?.bid_count,
    item?.numberOfBids,
    item?.numBids,
    item?.bidding?.bidCount,
    item?.sellingStatus?.bidCount,
  ];

  for (const candidate of candidates) {
    const count = Number.parseInt(String(candidate ?? '').replace(/,/g, ''), 10);
    if (Number.isInteger(count) && count >= 0) return count;
  }

  const reasonMatch = String(item?.reason || '').match(/\b([\d,]+)\s+bids?\b/i);
  if (!reasonMatch) return null;

  const count = Number.parseInt(reasonMatch[1].replace(/,/g, ''), 10);
  return Number.isInteger(count) ? count : null;
}

function getEbayAuctionUrl(item) {
  const candidates = [item?.viewItemURL, item?.url, item?.link];
  for (const candidate of candidates) {
    if (typeof candidate !== 'string' || !candidate.trim()) continue;
    try {
      const url = new URL(candidate);
      if (
        url.hostname === 'ebay.com' ||
        url.hostname === 'www.ebay.com' ||
        url.hostname.endsWith('.ebay.com')
      ) {
        return url.href;
      }
    } catch {
      // Ignore malformed source URLs and fall back to an eBay title search.
    }
  }

  const numericItemId = [item?.itemId, item?.item_id, item?.epid].find(
    (value) => /^\d{9,15}$/.test(String(value || ''))
  );
  if (numericItemId) {
    return `https://www.ebay.com/itm/${numericItemId}`;
  }

  const query = item?.title || item?.name || 'sports trading card';
  return `https://www.ebay.com/sch/i.html?_nkw=${encodeURIComponent(query)}&_sacat=261328&LH_Auction=1`;
}

function createItemElement(item) {
  const link = document.createElement('a');
  link.className = 'trending-item';
  link.href = getEbayAuctionUrl(item);
  link.target = '_blank';
  link.rel = 'noopener noreferrer';

  const media = document.createElement('div');
  media.className = 'trending-item__media';
  media.innerHTML =
    '<div class="auction-image-placeholder"><span class="auction-spotlight__eyebrow">Current bid</span><strong class="auction-spotlight__price">Live auction</strong><span class="auction-spotlight__activity"><i class="fas fa-gavel" aria-hidden="true"></i> Live bidding</span><span class="auction-spotlight__cta">View on eBay <i class="fas fa-arrow-right" aria-hidden="true"></i></span></div>';
  renderAuctionImage(media, item, link);
  link.appendChild(media);

  const badge = document.createElement('span');
  badge.className = 'card-context-badge';
  badge.textContent = 'Live auction';
  link.appendChild(badge);

  const title = document.createElement('p');
  title.className = 'trending-title';
  title.textContent = item.title || '';
  link.appendChild(title);

  const price = document.createElement('p');
  price.className = 'trending-price';
  // Robustly extract price from different API shapes
  const extractPrice = (it) => {
    // Helper to coerce string like "$202.50" or "202.50" to number
    const parseMaybeCurrency = (v) => {
      if (typeof v === 'number' && !Number.isNaN(v)) return v;
      if (typeof v === 'string') {
        const n = parseFloat(v.replace(/[^0-9.\-]/g, ''));
        return Number.isNaN(n) ? null : n;
      }
      return null;
    };

    // Try common shapes in order of specificity
    // 1) item.price as object { value, raw, currency }
    if (it && typeof it.price === 'object' && it.price !== null) {
      const amount = parseMaybeCurrency(it.price.value ?? it.price.raw);
      const currency =
        it.price.currency ||
        it.price.currencyId ||
        it.priceCurrency ||
        it.currency ||
        null;
      const raw = typeof it.price.raw === 'string' ? it.price.raw : null;
      if (amount !== null || raw) return { amount, currency, raw };
    }

    // 2) item.prices as array of { value, raw }
    if (Array.isArray(it?.prices) && it.prices.length > 0) {
      const first = it.prices[0];
      const amount = parseMaybeCurrency(first?.value ?? first?.raw);
      const currency =
        first?.currency ||
        first?.currencyId ||
        it.priceCurrency ||
        it.currency ||
        null;
      const raw = typeof first?.raw === 'string' ? first.raw : null;
      if (amount !== null || raw) return { amount, currency, raw };
    }

    // 3) item.currentPrice as number/object
    if (it?.currentPrice !== undefined) {
      if (typeof it.currentPrice === 'object' && it.currentPrice !== null) {
        const amount = parseMaybeCurrency(
          it.currentPrice.value ?? it.currentPrice.raw
        );
        const currency =
          it.currentPrice.currency ||
          it.currentPrice.currencyId ||
          it.priceCurrency ||
          it.currency ||
          null;
        const raw =
          typeof it.currentPrice.raw === 'string' ? it.currentPrice.raw : null;
        if (amount !== null || raw) return { amount, currency, raw };
      } else {
        const amount = parseMaybeCurrency(it.currentPrice);
        if (amount !== null)
          return {
            amount,
            currency: it.priceCurrency || it.currency || null,
            raw: null,
          };
      }
    }

    // 4) eBay Finding API shape: sellingStatus.currentPrice
    const ss = it?.sellingStatus?.currentPrice;
    if (ss !== undefined) {
      if (typeof ss === 'object' && ss !== null) {
        const amount = parseMaybeCurrency(ss.value ?? ss.__value__ ?? ss.raw);
        const currency =
          ss.currency ||
          ss.currencyId ||
          it.priceCurrency ||
          it.currency ||
          null;
        const raw = typeof ss.raw === 'string' ? ss.raw : null;
        if (amount !== null || raw) return { amount, currency, raw };
      } else {
        const amount = parseMaybeCurrency(ss);
        if (amount !== null)
          return {
            amount,
            currency: it.priceCurrency || it.currency || null,
            raw: null,
          };
      }
    }

    // 5) item.price as primitive number/string
    if (
      it?.price !== undefined &&
      it.price !== null &&
      typeof it.price !== 'object'
    ) {
      const amount = parseMaybeCurrency(it.price);
      if (amount !== null)
        return {
          amount,
          currency: it.priceCurrency || it.currency || null,
          raw: null,
        };
    }

    return { amount: null, currency: null, raw: null };
  };

  const { amount, currency, raw } = extractPrice(item);
  let formattedPrice = raw;
  if (amount !== null || raw) {
    if (amount !== null) {
      formattedPrice = amount.toLocaleString(undefined, {
        style: 'currency',
        currency: currency || 'USD',
      });
      price.textContent = formattedPrice;
    } else if (raw) {
      // Fallback: use raw string like "$202.50"
      price.textContent = raw;
    }
    link.appendChild(price);
  }

  const spotlightPrice = media.querySelector('.auction-spotlight__price');
  const spotlightEyebrow = media.querySelector('.auction-spotlight__eyebrow');
  if (formattedPrice) {
    spotlightPrice.textContent = formattedPrice;
  } else {
    spotlightEyebrow.textContent = 'Auction status';
  }

  const bidCount = getAuctionBidCount(item);
  if (bidCount !== null) {
    const activity = media.querySelector('.auction-spotlight__activity');
    activity.innerHTML = `<i class="fas fa-gavel" aria-hidden="true"></i> ${bidCount} ${
      bidCount === 1 ? 'bid' : 'bids'
    }`;
  }

  return link;
}

function createCardElement(card) {
  const container = document.createElement('div');
  container.className = 'featured-card-container';

  const link = document.createElement('a');
  link.className = 'featured-card';
  const url = card.url || card.link || card.viewItemURL || null;
  if (url) {
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }

  const img = document.createElement('img');
  img.src =
    card.image ||
    card.imageUrl ||
    card.image_url ||
    card.frontImage ||
    card.front_image ||
    '';
  img.alt = card.title || card.name || 'Card image';
  img.addEventListener(
    'error',
    () => {
      img.src = 'images/trading_cards.jpg';
    },
    { once: true }
  );
  link.appendChild(img);

  const badge = document.createElement('span');
  badge.className = 'card-context-badge';
  badge.textContent = 'In collection';
  link.appendChild(badge);

  const title = document.createElement('p');
  title.className = 'featured-title';
  title.textContent = card.title || card.name || '';
  link.appendChild(title);

  container.appendChild(link);

  const ownerUsername =
    card.ownerUsername || card.owner_username || card.username || null;
  if (ownerUsername) {
    const button = document.createElement('a');
    button.className = 'subtle-button view-collection-button';
    button.textContent = 'View collection';
    button.href = `/profile.html?username=${ownerUsername}`;
    container.appendChild(button);
  }

  return container;
}

function createTweetElement(tweet) {
  const url =
    tweet.url || tweet.tweet_url || tweet.link || tweet.permalink || tweet.id;
  if (!url) return document.createDocumentFragment();
  const blockquote = document.createElement('blockquote');
  blockquote.className = 'twitter-tweet';
  const anchor = document.createElement('a');
  anchor.href = url;
  blockquote.appendChild(anchor);
  return blockquote;
}

function renderSkeletons(container, count = 5) {
  if (!container) return;
  const fragment = document.createDocumentFragment();
  for (let index = 0; index < count; index += 1) {
    const skeleton = document.createElement('div');
    skeleton.className = 'discover-card-skeleton';
    skeleton.setAttribute('aria-hidden', 'true');
    fragment.appendChild(skeleton);
  }
  container.appendChild(fragment);
}

function renderEmptyState(container, message) {
  if (!container) return;
  container.replaceChildren();
  const empty = document.createElement('div');
  empty.className = 'discover-empty-state';
  empty.innerHTML = '<i class="fas fa-layer-group" aria-hidden="true"></i>';
  const text = document.createElement('p');
  text.textContent = message;
  empty.appendChild(text);
  container.appendChild(empty);
}

function parseCount(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(0, value);
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    if (Number.isFinite(parsed)) {
      return Math.max(0, parsed);
    }
  }

  return null;
}

function getDefaultCollectionId(user) {
  const defaultCollection = user?.default_collection || {};
  return defaultCollection.id ?? user?.collection_id ?? null;
}

function getCollectorDisplayName(user) {
  const rawName =
    user?.display_name || user?.displayName || user?.username || user?.email;

  if (!rawName || typeof rawName !== 'string') {
    return 'collector';
  }

  const noDomain = rawName.includes('@') ? rawName.split('@')[0] : rawName;
  const normalized = noDomain
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalized || 'collector';
}

async function resolveCollectionCardCount(headers, user) {
  const defaultCollection = user?.default_collection || {};
  const inlineCountCandidates = [
    defaultCollection.card_count,
    defaultCollection.cards_count,
    defaultCollection.cardCount,
    defaultCollection.total_cards,
    defaultCollection.totalCards,
    user?.card_count,
    user?.cards_count,
  ];

  for (const candidate of inlineCountCandidates) {
    const parsed = parseCount(candidate);
    if (parsed !== null) {
      return parsed;
    }
  }

  const collectionId = getDefaultCollectionId(user);
  if (collectionId === null || collectionId === undefined) {
    return null;
  }

  try {
    const result = await sendAuthenticatedRequest(
      `${config.detectionUrl}/collections/${collectionId}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!result.ok) {
      return null;
    }

    const body = result.body || {};
    if (Array.isArray(body.cards)) {
      return body.cards.length;
    }

    const bodyCountCandidates = [
      body.card_count,
      body.cards_count,
      body.cardCount,
      body.total_cards,
      body.totalCards,
    ];

    for (const candidate of bodyCountCandidates) {
      const parsed = parseCount(candidate);
      if (parsed !== null) {
        return parsed;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Unable to resolve collection card count:', error);
  }

  return null;
}

function buildHeroState(displayName, cardCount) {
  if (cardCount === 0) {
    return {
      eyebrow: `Welcome back, ${displayName}`,
      title: 'Kick off your collection with your first card',
      description:
        'The fastest way to get value from CardSense is to scan one card now and let us build your collection timeline.',
      ctaText: 'Add your first card',
      ctaHref: '/dashboard.html',
      cardCountText: '0',
      cardCountNote:
        'No cards yet. Your first upload starts your collection history.',
      nextStep: 'Start with a single scan',
    };
  }

  if (typeof cardCount === 'number' && cardCount > 0) {
    const cardWord = cardCount === 1 ? 'card' : 'cards';
    return {
      eyebrow: `Good to see you, ${displayName}`,
      title: 'Your collection is moving, keep the momentum',
      description:
        'Add another card now to stay active, grow your public profile, and keep your collection up to date.',
      ctaText: 'Add another card',
      ctaHref: '/dashboard.html',
      cardCountText: cardCount.toLocaleString(),
      cardCountNote: `${cardCount.toLocaleString()} ${cardWord} tracked so far.`,
      nextStep: 'Add one more card today',
    };
  }

  return {
    eyebrow: `Welcome back, ${displayName}`,
    title: 'Discover what collectors are tracking',
    description:
      'Browse hot auctions, newly scanned cards, and jump into your scanner when you are ready to add your next card.',
    ctaText: 'Add a card now',
    ctaHref: '/dashboard.html',
    cardCountText: '-',
    cardCountNote: 'Open your collection to sync your latest count.',
    nextStep: 'Open scanner and upload',
  };
}

function renderHeroState(state) {
  const eyebrow = document.getElementById('discover-hero-eyebrow');
  const title = document.getElementById('discover-hero-title');
  const description = document.getElementById('discover-hero-description');
  const cta = document.getElementById('hero-add-card-cta');
  const cardCount = document.getElementById('discover-hero-card-count');
  const cardCountNote = document.getElementById('discover-hero-count-note');
  const nextStep = document.getElementById('discover-hero-next-step');

  if (
    !eyebrow ||
    !title ||
    !description ||
    !cta ||
    !cardCount ||
    !cardCountNote ||
    !nextStep
  ) {
    return;
  }

  eyebrow.textContent = state.eyebrow;
  title.textContent = state.title;
  description.textContent = state.description;
  cta.textContent = state.ctaText;
  cta.href = state.ctaHref;
  cardCount.textContent = state.cardCountText;
  cardCountNote.textContent = state.cardCountNote;
  nextStep.textContent = state.nextStep;
}

async function personalizeDiscoverHero(headers, user) {
  const displayName = getCollectorDisplayName(user);
  const cardCount = await resolveCollectionCardCount(headers, user);
  renderHeroState(buildHeroState(displayName, cardCount));
}

async function init() {
  const featuredGrid = document.getElementById('featured-grid');
  const recentGrid = document.getElementById('recent-grid');
  const grid = document.getElementById('trending-grid');
  const tweetRow = document.getElementById('tweets-row');

  renderSkeletons(featuredGrid);
  renderSkeletons(recentGrid);
  renderSkeletons(grid);

  const [featuredCards, recentCards, items, tweets] = await Promise.all([
    fetchFeaturedCards(),
    fetchRecentlyAddedCards(),
    fetchTrendingItems(),
    fetchTrendingTweets(),
  ]);
  if (featuredGrid) {
    featuredGrid.replaceChildren();
    if (featuredCards.length) {
      featuredCards.forEach((card) => {
        featuredGrid.appendChild(createCardElement(card));
      });
    } else {
      renderEmptyState(featuredGrid, 'Featured cards will appear here soon.');
    }
  }
  if (recentGrid) {
    recentGrid.replaceChildren();
    if (recentCards.length) {
      recentCards.forEach((card) => {
        recentGrid.appendChild(createCardElement(card));
      });
    } else {
      renderEmptyState(recentGrid, 'No new scans yet. Check back shortly.');
    }
  }
  if (grid) {
    grid.replaceChildren();
    if (items.length) {
      items.forEach((item) => {
        grid.appendChild(createItemElement(item));
      });
    } else {
      renderEmptyState(grid, 'Live auctions are taking a quick break.');
    }
  }
  if (tweetRow) {
    if (tweets.length) {
      tweets.forEach((t) => {
        tweetRow.appendChild(createTweetElement(t));
      });
    } else {
      renderEmptyState(tweetRow, 'Hobby conversation will appear here soon.');
    }
  }
  if (window.twttr && window.twttr.widgets && window.twttr.widgets.load) {
    window.twttr.widgets.load();
  }
}

let discoverInitPromise = null;

function ensureDiscoverInitialized() {
  if (!discoverInitPromise) {
    discoverInitPromise = init().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Failed to load discover content:', error);
      discoverInitPromise = null;
    });
  }
  return discoverInitPromise;
}

async function handleAuthenticatedUser(_user, token) {
  ensureDiscoverInitialized();

  if (!token) return;

  try {
    const headers = setupUserToken(token);
    let serversideUser = await getUser(headers);

    if (!serversideUser?.username || serversideUser.username.trim() === '') {
      await showProfileSetupWizardAndWait(token);
      serversideUser = await getUser(headers);
    }

    await personalizeDiscoverHero(headers, serversideUser);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error handling authenticated discover user:', error);
  }
}

function handleUnauthenticatedUser() {
  const currentUrl = window.location.href;
  window.location.href = `/login.html?redirect=${encodeURIComponent(currentUrl)}`;
}

let hasBootstrappedDiscover = false;

function startDiscoverPage() {
  if (hasBootstrappedDiscover) return;
  hasBootstrappedDiscover = true;
  ensureDiscoverInitialized();
  setupAuthentication(handleAuthenticatedUser, handleUnauthenticatedUser);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startDiscoverPage, {
    once: true,
  });
} else {
  startDiscoverPage();
}

/* =====================================================
   CardSense Discover — section spice pack (JS helpers)
   - Scroll reveal for cards/tweets
   - Auto-observe dynamically added items
   - Horizontal wheel scroll for Tweets row
   - Safe to drop into existing bundle
   ===================================================== */
(() => {
  // Avoid double-installing if this file is loaded more than once
  if (window.__cardsenseDiscoverEnhancementsInstalled) return;
  window.__cardsenseDiscoverEnhancementsInstalled = true;

  const CONTAINERS = [
    '#featured-grid',
    '#recent-grid',
    '#trending-grid',
    '#tweets-row',
  ];
  const ITEM_SELECTOR =
    '.card, .featured-card, .trending-item, .twitter-tweet, .tweet-card';

  // IntersectionObserver for reveal-on-scroll
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
  );

  // Mark all matching children of a container for reveal + stagger them
  function markReveal(container) {
    if (!container) return;
    const items = container.querySelectorAll(ITEM_SELECTOR);
    let i = 0;
    items.forEach((el) => {
      if (!el.classList.contains('reveal-on-scroll')) {
        el.classList.add('reveal-on-scroll');
      }
      // Apply a subtle stagger based on index within the row
      el.style.transitionDelay = `${(i % 8) * 30}ms`;
      io.observe(el);
      i++;
    });
    // Lazy: mark any images inside as lazy if not already
    container.querySelectorAll('img:not([loading])').forEach((img) => {
      img.setAttribute('loading', 'lazy');
      img.setAttribute('decoding', 'async');
    });
  }

  // Observe DOM changes for dynamic population of grids
  function watchContainer(selector) {
    const el = document.querySelector(selector);
    if (!el) return;
    markReveal(el);
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        // Consider only added nodes
        m.addedNodes &&
          m.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            if (node.matches(ITEM_SELECTOR)) {
              markReveal(el);
            } else {
              // If a wrapper was added that contains items
              const maybeItems = node.querySelectorAll
                ? node.querySelectorAll(ITEM_SELECTOR)
                : [];
              if (maybeItems.length) markReveal(el);
            }
          });
      }
    });
    mo.observe(el, { childList: true, subtree: true });
  }

  // Enhance horizontal scrolling for Tweets row (mouse wheel -> horizontal)
  function enhanceTweetRowWheel() {
    const tweets = document.querySelector('#tweets-row');
    if (!tweets) return;
    tweets.addEventListener(
      'wheel',
      (e) => {
        // If vertical scroll dominates, translate it to horizontal
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
          tweets.scrollLeft += e.deltaY;
          e.preventDefault();
        }
      },
      { passive: false }
    );
  }

  // Public API (optional): window.CardSenseDiscover.markReveal(containerOrSelector)
  function exposeAPI() {
    const api = {
      markReveal: (target) => {
        const el =
          typeof target === 'string' ? document.querySelector(target) : target;
        markReveal(el);
      },
    };
    window.CardSenseDiscover = Object.assign(
      window.CardSenseDiscover || {},
      api
    );
  }

  // Boot on DOM ready, but also handle cases where this script loads late
  function boot() {
    CONTAINERS.forEach(watchContainer);
    enhanceTweetRowWheel();
    exposeAPI();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
